import type { KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { formatInteger, formatUsd } from '../../demo/format'
import { useAdsByCampaign, useCreativeLibrary } from '../../demo/selectors'
import type { Ad, AnalyticsLens, CreativeLibraryItem, ProductLensRow } from '../../demo/types'
import { Badge, GlossaryText, Icon, Term } from '../../ui'
import { lensCopy } from './lensCopy'
import { formatRatio, spreadProductRow } from './lensMath'
import styles from './ProductAdRows.module.css'

interface ProductAdRowsProps {
  row: ProductLensRow
  lens: AnalyticsLens
}

type BadgeTone = 'green' | 'red' | 'orange'

function assetUrl(creative: CreativeLibraryItem | undefined): string {
  if (!creative) return ''
  if (creative.dataUrl) return creative.dataUrl
  return creative.cover.startsWith('/') ? creative.cover : `/${creative.cover}`
}

function adStatus(ad: Ad, creative: CreativeLibraryItem | undefined): { label: string; tone: BadgeTone } {
  if (ad.status === 'rejected') return { label: lensCopy.status.rejected, tone: 'red' }
  if (ad.status === 'throttled') return { label: lensCopy.status.throttled, tone: 'orange' }
  if (creative?.compliance?.level === 'risk') return { label: lensCopy.status.risk, tone: 'orange' }
  return { label: lensCopy.status.live, tone: 'green' }
}

export function ProductAdRows({ row, lens }: ProductAdRowsProps) {
  const navigate = useNavigate()
  const ads = useAdsByCampaign(row.campaignId)
  const creatives = useCreativeLibrary()
  const creativeById = new Map(creatives.map((creative) => [creative.id, creative]))
  const metrics = spreadProductRow(row, ads, lens)
  const openCampaign = () => {
    void navigate(`/monitor/${row.campaignId}`)
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.querySelector('[data-scope="detail-creatives"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    })
  }
  const openFromKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openCampaign()
    }
  }

  return (
    <tr className={styles.expandedRow}>
      <td colSpan={9}>
        {ads.map((ad, index) => {
          const creative = creativeById.get(ad.creativeId)
          const status = adStatus(ad, creative)
          const values = metrics[index]
          return (
            <div
              className={styles.adRow}
              key={ad.id}
              role="link"
              tabIndex={0}
              aria-label={lensCopy.openAd(ad.name)}
              onClick={openCampaign}
              onKeyDown={openFromKeyboard}
            >
              <img className={styles.cover} src={assetUrl(creative)} alt={creative?.name ?? ad.name} />
              <div className={styles.creative}>
                <p className={styles.name}>{creative?.name ?? ad.name}</p>
                <p className={styles.meta}>{creative?.kind === 'video' ? lensCopy.creativeVideo(creative.duration) : lensCopy.creativeImage}</p>
              </div>
              <Badge tone={status.tone}><GlossaryText text={status.label} /></Badge>
              <div className={styles.metric}><p>{lensCopy.metrics.spend}</p><p className={styles.metricValue}>{formatUsd(values?.spend ?? 0)}</p></div>
              <div className={styles.metric}><p>{lensCopy.metrics.impressions}</p><p className={styles.metricValue}>{formatInteger(values?.impressions ?? 0)}</p></div>
              <div className={styles.metric}><p><Term k="ctr" /></p><p className={styles.metricValue}>{formatRatio(ad.clicks, ad.impressions)}</p></div>
              <div className={styles.metric}><p><Term k="cvr" /></p><p className={styles.metricValue}>{formatRatio(ad.orders, ad.clicks)}</p></div>
              <div className={styles.metric}>
                <p>{lens === 'sales' ? lensCopy.metrics.orders : lensCopy.metrics.reach}</p>
                <p className={styles.metricValue}>{formatInteger(lens === 'sales' ? values?.orders ?? 0 : values?.reach ?? 0)}</p>
              </div>
              <Icon className={styles.chevron} name="chevron-right" size={17} strokeWidth={2} />
            </div>
          )
        })}
      </td>
    </tr>
  )
}
